import React, { useState, useEffect } from 'react';
import { Helmet } from 'rnd-helmet';
import { injectIntl } from 'react-intl';
import {Redirect, Route, useRouteMatch} from "react-router-dom";
import TextHeader from "@UI/HeaderText";
import ViewSwitcher from "@Components/ViewSwitcher";
import Pager from "@Components/Pager";
import axios from "axios";
import BranchListItem from "@Components/BranchListItem";
import BranchPinItem from "@Components/BranchPinItem";
import BranchFilters from "@Components/BranchFilters";
import buildSearchListPager from "buildSearchListPager";
import { List } from "react-content-loader";
import dispatchRerender from "dispatchRerender";
import GetRouteFromUrl from "GetRouteFromUrl";
import searchParamsParser from "@Utils/searchParamsParser";
import { generateDataLayer } from "dataLayerHelper";
import stringSanitizer from "stringSanitizer";
import Breadcrumbs from "@Components/Breadcrumbs";
import searchParamsStringify from "@Utils/searchParamsStringify";
import LinkList from "@Components/LinkList";
import Optimizely from '@TrackingComponents/Optimizely';

function Branches({ intl, staticContext, match, location }) {
  const { locale } = intl;
  let initialData = {};
  let searchParams = {};
  const route = useRouteMatch();
  const routeConfig = GetRouteFromUrl();

  if (staticContext && staticContext.data) {
    initialData = staticContext.data;
    // Pass the dataLayer to the server using staticContext.
    if (staticContext.data.appConfig && staticContext.data.appConfig.gtm) {
      const gtm = staticContext.data.appConfig.gtm;
      if (gtm.hasOwnProperty("enabled") && gtm.enabled) {
        staticContext.dataLayer = generateDataLayer("office_locator_list", locale);
      }
    }
  }
  else if (typeof window !== "undefined" && window.__ROUTE_DATA__) {
    initialData = window.__ROUTE_DATA__;
  }

  let optimizelySettings;
  if (typeof window !== 'undefined' && window.__OPTIMIZELY_SETTINGS__) {
    optimizelySettings = window.__OPTIMIZELY_SETTINGS__;
  } else {
    optimizelySettings = staticContext?.optimizelySettings || {};
  }

  if (initialData && initialData.searchParams) {
    searchParams = initialData.searchParams;
  }
  else if (match.params.searchParams) {
    searchParams = searchParamsParser(match.params.searchParams);
  }

  const [state, setState] = useState(initialData);
  const locationIsValid = state.geoData && Object.keys(state.geoData).length !== 0;

  useEffect(() => {
    document.querySelector('body').classList.add('has-filter-bar')
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined' && window.__ROUTE_DATA__) {
      setState(window.__ROUTE_DATA__);
      delete window.__ROUTE_DATA__;
    }
    else {
      const callbackAlias = '@Callbacks/getSearchResults';
      const data = {
        language: intl.locale,
      };

      axios.post('/api/branches/get-callback', {
        callback: callbackAlias,
        currentRoute: { ...match, ...{ routeName: "our-offices" }, ...{ params: { searchParams: searchParamsStringify(searchParams) } } },
        data: data
      }).then(response => {
        setState(response.data);
        dispatchRerender();
      })
    }
  }, [match.params.searchParams, location.search]);

  if (!Object.keys(state).length) {
    return (
      <>
        <Helmet>
          <header className="header header--text bg-variant-brand-secondary" />
        </Helmet>
        <div className="block__wrapper wrapper">
          <div className="filter">
            <List uniqueKey="filter-list" />
          </div>
          <div className="block__content block__content--s block__content--align-right">
            <List uniqueKey="content-list" />
          </div>
        </div>
      </>
    );
  }

  let baseUrl = route.path.replace(':searchParams*', '');
  if (searchParams && searchParams.page && parseInt(searchParams.page) === 1) {
    return <Route render={({staticContext}) => {
      if (staticContext) staticContext.status = 301;
      return <Redirect to={`${baseUrl}${searchParamsStringify({...searchParams, ...{page: null}})}`}/>
    }}/>
  }

  let noResultsContent = [];
  let contentView = null;
  let pager = {};
  let total = 0;
  let noResults = false;
  if (state.searchResults && state.searchResults.hits && state.searchResults.hits.hits.length > 0) {
    total = state.searchResults.hits.total;
    let branchesList = [];
    let branchesPins = [];
    for (let id in state.searchResults.hits.hits) {
      const branchData = state.searchResults.hits.hits[id]['_source'];
      const distance = Math.ceil(state.searchResults.hits.hits[id]['sort']);
      branchesList.push(<BranchListItem key={id} branchData={branchData} distance={distance} appConfigs={state.appConfig} />)
      branchesPins.push(<BranchPinItem key={id} branchData={branchData} />)
    }

    pager = buildSearchListPager(searchParams, total, match.path.replace(':searchParams*', ''), state.appConfig);
    if (searchParams.type === "map") {
      contentView =
        <div className="map" data-rs-maps={`${process.env.REACT_APP_MAP_CENTER_LAT},${process.env.REACT_APP_MAP_CENTER_LNG}`}
          data-rs-maps-zoom={process.env.REACT_APP_MAP_ZOOM} data-rs-maps-google-maps-key={state.appKeys.google_maps_api_key || ''} data-rs-maps-marker-url="/branches/assets/image/icons/marker.png">
          <ul className="popup-bubbles hidden">
            {branchesPins}
          </ul>
          <div className="map__gmaps" data-rs-maps-placeholder={true} />
        </div>;
    }
    else {
      contentView =
        <>
          <div className="map__list">
            <ul className="list-items">
              {branchesList}
            </ul>
          </div>
          <Pager previous={pager.previous} pagesList={pager.pages} next={pager.next} />
        </>;
    }
  }
  else {
    // Fix 404-page redirect.
    if (staticContext && !locationIsValid) {
      staticContext.status = 404;
    }
    noResults = true;
    noResultsContent = state.appConfig && state.appConfig.no_result_text ?
      <div dangerouslySetInnerHTML={{ __html: state.appConfig.no_result_text }} /> :
      <>
        <div>{intl.formatMessage({ id: "Branches.NoResultsFirstLine" })}</div>
        <br />
        <div>
          {
            intl.formatMessage({ id: "Branches.NoResultsSecondLine" }, {
              link: <a href={match.path.replace(":searchParams*", "")}>{intl.formatMessage({ id: "Branches.NoResultsLinkText" })}</a>
            }
            )
          }
        </div>
      </>
  }

  // There two cases where the branch locator pages must be indexable by bots.
  // The first one is when no filters are applied, and this is always true.
  // The second one is when a specialism filter is applied.
  // In any other cases a "noindex" tag is added to the page.
  // REACT_APP_INDEX_SPECIALISM_PAGES variable controls on a high level
  // should we index or not the pages with a specialism filter. It is
  // set to TRUE by default, but it can be overridden by OpCos.
  // In order to have specialism pages indexable, we must have a
  // valid specialism filter, meaning that the specialism search param
  // must exists in the elasticsearch index.
  let indexPage = false;
  if (match.url === match.path.replace(':searchParams*', '')) {
    // The default page with no filters is always indexed.
    indexPage = true;
  } else {
    const isSpecialismPage = Object.keys(searchParams).length === 1 && searchParams.specialism !== undefined;
    if (isSpecialismPage && process.env.REACT_APP_INDEX_SPECIALISM_PAGES) {
      let isValidSpecialism = false;
      for (let id in state.specialismOptions) {
        const specialismData = state.specialismOptions[id];
        if (stringSanitizer(specialismData.name) === searchParams.specialism) {
          isValidSpecialism = true;
          break;
        }
      }

      if (isValidSpecialism) {
        indexPage = true;
      }
    }
  }
  const metaUrl = `${process.env.REACT_APP_BASE_URL}${baseUrl}${searchParamsStringify(searchParams)}`;
  const metaIndex = indexPage ? "index, follow" : "noindex";
  let pageTitle = intl.formatMessage({ id: "Branches.Title" }, { sitename: process.env.REACT_APP_SITENAME });
  let metaTitle = intl.formatMessage({ id: "Branches.MetaTitle" }, { sitename: process.env.REACT_APP_SITENAME });
  if (searchParams.hasOwnProperty('page') && intl.messages["Global.Page"]) {
    pageTitle = `${intl.formatMessage({id: "Global.Page"}, {page: searchParams.page})} ${pageTitle}`
    metaTitle = `${intl.formatMessage({id: "Global.Page"}, {page: searchParams.page})} ${metaTitle}`
  }
  const metaOgImage = `${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_META_OG_IMAGE}`;

  return (
    <>
      <Helmet>
        <header className="header header--text bg-variant-brand-secondary" />
        <title>{pageTitle}</title>
        <link rel="canonical" href={metaUrl} />
        <meta property="og:url" content={metaUrl} />
        <meta name="twitter:url" content={metaUrl} />
        <meta name="title" content={metaTitle} />
        <meta name="twitter:title" content={metaTitle} />
        <meta property="og:title" content={metaTitle} />
        <meta name="description" content={intl.formatMessage({ id: "Branches.MetaDescription" })} />
        <meta name="twitter:description" content={intl.formatMessage({ id: "Branches.MetaDescription" })} />
        <meta property="og:description" content={intl.formatMessage({ id: "Branches.MetaDescription" })} />
        <meta name="og:image" content={metaOgImage} />
        <meta name="twitter:image" content={metaOgImage} />
        <meta name="robots" content={metaIndex} />
      </Helmet>
      <Optimizely optimizelySettings={optimizelySettings} />
      <div className="navigation__wrapper bg-variant-brand-secondary">
        <div className="wrapper">
          <Breadcrumbs breadcrumbs={routeConfig.breadcrumbs} data={state} currentRoute={route}/>
        </div>
      </div>
      <TextHeader
        titleTop={intl.formatMessage({ id: "Branches.TitleTop" })}
        titleBottom={intl.formatMessage({ id: "Branches.TitleBottom" })}
        bgColor="secondary"
      >
        {intl.messages["BranchLocator.text"] ? intl.formatMessage({ id: "Branches.Text" }) : null}
      </TextHeader>
      <ViewSwitcher resultsTotal={total} searchParams={searchParams} />
      <div className="block block--filter">
        <div className="block__wrapper wrapper">
          <BranchFilters searchParams={state.searchParams} regionList={state.regionList} specialismOptions={state.specialismOptions} appConfigs={state.appConfig} resultsTotal={total} />
          <div className="block__content block__content--s block__content--align-right">
            {noResults ? noResultsContent : contentView}
          </div>
        </div>
      </div>
      <LinkList appSettings={state.appConfig}/>
    </>
  );
}

export default injectIntl(Branches);
